"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────
   1. Insider tips for success
   ───────────────────────────────────────────── */
export function PartnerInsiderTips({ section }) {
  return (
    <section className="overflow-visible py-16 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Floating Magic Wand icon at top, overlapping the dome edge */}
        <div className="relative z-20 ml-0 lg:ml-16 mb-[-48px]">
          <Image
            src="/odoo/icons/green_magic-wand.svg"
            width={80}
            height={80}
            className="w-auto select-none pointer-events-none h-20"
            alt=""
          />
        </div>

        {/* First Row of columns: Title and main visual mockup */}
        <div className="row g-5 align-items-center mb-12 relative pt-16 pb-10">
          {/* Left-aligned, right-rounded dome backdrop shape covering the first row */}
          <div className="absolute top-0 bottom-0 left-[-50vw] right-[-2%] md:right-[5%] lg:right-[8%] bg-white/[0.03] rounded-r-[180px] md:rounded-r-[240px] -z-10 pointer-events-none select-none" />

          <div className="col-lg-6 pb-4">
            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight leading-tight mb-6 text-white ${caveat.className}`}>
              <span className="x_wd_green_highlight_03 mr-3 text-white">Insider tips</span>
              for success
            </h2>
            <p className="text-gray-300 leading-relaxed font-normal text-xl">
              <strong className="text-white font-bold">Pre-defined services.</strong> Explore service packs based on tried and tested Success Packs sold at Odoo. Each service automatically creates a project to ensure seamless implementation.
            </p>
          </div>
          <div className="col-lg-6 pb-4">
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.55)] overflow-hidden hover:scale-[1.01] transition-transform duration-300"
            >
              <Image
                src="/odoo/industries/odoo-partner/odoo-partner-insider-tips.webp"
                width={600}
                height={400}
                className="w-full h-auto object-contain"
                alt="Insider tips"
              />
            </motion.div>
          </div>
        </div>

        {/* Second Row of descriptions */}
        <div className="row g-5 pt-8 pb-4">
          <div className="col-lg-6">
            <p className="text-gray-300 leading-relaxed font-normal text-xl">
              <strong className="text-white font-bold">Game-changing quote calculator.</strong> Add your sales order lines, applications to implement, and the number of users, then sit back and let Odoo recommend the perfect package for your client.
            </p>
          </div>
          <div className="col-lg-6 relative">
            <p className="text-gray-300 leading-relaxed font-normal mb-8 text-xl">
              <strong className="text-white font-bold">Next-level flexibility.</strong> Modify the calculator with your own service packs, different ratios, and more, to adjust it to your unique business needs.
            </p>
            {/* Swerving grey arrow below content */}
            <Image
              src="/odoo/arrows/grey_arrow_lg_01.svg"
              width={130}
              height={130}
              style={{ filter: "brightness(0) invert(1)", opacity: 0.35, height: "130px", width: "auto" }}
              className="d-none d-lg-block mx-auto select-none pointer-events-none"
              alt=""
            />
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. Spectacular quotations in a few clicks
   ───────────────────────────────────────────── */
export function PartnerQuoteBuilder({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row g-5 align-items-center justify-content-between">
          
          {/* Right Column: Text descriptions on desktop */}
          <div className="col-lg-6 order-2 order-lg-2 space-y-6 pb-2 pb-lg-5">
            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight leading-tight mb-8 text-white ${caveat.className}`}>
              Spectacular{" "}
              <span className="x_wd_blue_highlight_05 text-white">quotations</span>{" "}
              in a few clicks
            </h2>
            <div className="space-y-6">
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Pre-configured quotation templates</strong> allow you to choose from Success Packs, ROI Analysis, and Large Implementations. Once confirmed, project and tasks are automatically created for implementation follow-up.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Stunning quotations.</strong> All you need is a couple of clicks to build a custom quotation dedicated to your client's unique business needs.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Success stories.</strong> Benefit from a library of success stories for various industries, and add them to your quotations in one click.
              </p>
            </div>
          </div>

          {/* Left Column: Overlapping images on desktop */}
          <div className="col-lg-5 order-1 order-lg-1 relative min-h-[380px] lg:min-h-[480px] flex items-center justify-center pb-2 pb-lg-5">
            {/* Backdrop circle glow */}
            <div className="absolute inset-0 bg-sky-500/5 rounded-full blur-[140px] pointer-events-none -z-10" />

            {/* Light circular background matching x_wd_shape_circle */}
            <div className="absolute top-[5%] bottom-[5%] left-[5%] right-[5%] bg-white/[0.03] rounded-full -z-10 pointer-events-none select-none" />

            {/* Top-Left Image with Blue Star Doodle */}
            <div className="absolute top-[8%] left-[5%] w-[58%] z-10 x_wd_corner_highlight_03">
              <Image
                src="/odoo/industries/odoo-partner/odoo partners-quote-builder-1.webp"
                width={400}
                height={300}
                className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] object-contain w-full h-auto"
                alt="Quote template builder"
              />
            </div>

            {/* Bottom-Right Image overlaying foreground */}
            <div className="absolute top-[32%] left-[38%] w-[58%] z-20">
              <Image
                src="/odoo/industries/odoo-partner/odoo partners-quote-builder-2.webp"
                width={400}
                height={300}
                className="rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] object-contain w-full h-auto"
                alt="Quotation layout example"
              />
            </div>
          </div>

        </div>

        {/* Transition dot arrow pointing down to next section */}
        <div className="w-full flex justify-center mt-16 select-none pointer-events-none">
          <Image
            src="/odoo/arrows/grey_arrow_dot_02.svg"
            width={140}
            height={140}
            style={{ height: "140px", width: "auto" }}
            className="brightness-0 invert opacity-40 transform rotate-[30deg] h-[140px] w-auto"
            alt=""
          />
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Efficient project management
   ───────────────────────────────────────────── */
export function PartnerProjectManagement({ section }) {
  return (
    <section className="overflow-visible pt-20 pb-36 lg:pb-48 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Crown Icon */}
        <div className="flex justify-center mb-6">
          <Image
            src="/odoo/icons/crown.svg"
            width={100}
            height={100}
            style={{ height: "100px", width: "auto", filter: "hue-rotate(-30deg) saturate(1.6) brightness(0.95)" }}
            className="w-auto select-none pointer-events-none"
            alt=""
          />
        </div>

        {/* Centered Heading */}
        <div className="text-center max-w-[1000px] mx-auto mb-16">
          <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight text-center ${caveat.className}`}>
            Efficient project{" "}
            <span className="x_wd_yellow_highlight_bold_04 ml-2">
              management
            </span>
          </h2>
        </div>

        {/* 3-Column Paragraph layout */}
        <div className="row justify-content-center pt-2 mb-16">
          <div className="col-lg-4 pb-5">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">All-in-one project hub.</strong> Manage all aspects of the implementation from one centralized dashboard. Use the top bar to handle all related records, from documents to sales orders.
            </p>
          </div>
          <div className="col-lg-4 pb-5">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Every hour counts.</strong> Log timesheets by hours or full days, using a desktop or a mobile device.
            </p>
          </div>
          <div className="col-lg-4 pb-5">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Detailed analytic overview.</strong> Easily keep track of timesheets, and expenses, and analyze cost distribution to ensure you turn a profit on each implementation.
            </p>
          </div>
        </div>

        {/* Main Mockup Container Card */}
        <div className="relative bg-white/[0.03] rounded-3xl p-8 lg:p-14 text-center shadow-2xl relative overflow-visible backdrop-blur-sm max-w-[1100px] mx-auto flex flex-col items-center z-10">
          {/* Subtle backdrop circle glow */}
          <div className="absolute inset-0 bg-amber-500/5 rounded-3xl blur-[120px] pointer-events-none -z-10" />

          {/* Squeezed downwards arrow doodle */}
          <Image
            src="/odoo/arrows/arrow_sm_03.svg"
            width={50}
            height={50}
            style={{ filter: "brightness(0) invert(1)", opacity: 0.5, height: "50px", width: "auto" }}
            className="w-auto select-none pointer-events-none mb-6 -mt-12 lg:-mt-20 z-20"
            alt=""
          />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full lg:w-[85%] rounded-2xl overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.65)] hover:scale-[1.01] transition-transform duration-300 mb-[-80px] lg:mb-[-140px] z-20 bg-[#151517]"
          >
            <Image
              src="/odoo/industries/odoo-partner/odoo partners-project-management.webp"
              width={900}
              height={600}
              className="w-full h-auto object-contain"
              alt="Odoo partners project dashboard"
            />
          </motion.div>
        </div>

      </div>
    </section>
  );
}
